import React, { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { Inline } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { ColumnPickerContainer } from '@atlassian/jira-issue-table-column-picker';
import AsyncColumnPicker, {
	type ColumnPickerProps,
} from '@atlassian/jira-issue-table-column-picker/src/async';
import {
	ContextualAnalyticsData,
	FireUiAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { ui_nativeIssueTable_NativeIssueTable_columns$key as ColumnsFragment } from '@atlassian/jira-relay/src/__generated__/ui_nativeIssueTable_NativeIssueTable_columns.graphql';
import type { ui_nativeIssueTable_NativeIssueTable_issues$key as IssuesFragment } from '@atlassian/jira-relay/src/__generated__/ui_nativeIssueTable_NativeIssueTable_issues.graphql';
import { ScrollStateContextProvider } from '@atlassian/jira-software-fast-virtual';
import {
	BORDER_WIDTH,
	COLUMN_ID_MEATBALL_MENU,
	COLUMN_OPERATION_RESIZE,
	COLUMN_TYPE_PARENT_LINK,
	DEFAULT_COLUMN_WIDTH,
	HEADER_HEIGHT,
	KNOWN_COLUMN_TYPES,
	MODIFY_COLUMNS,
	REORDER_COLUMNS,
	SORT_ASC,
	SORT_NONE,
} from '../common/constants';
import type {
	Column,
	ColumnConfigChangeType,
	ColumnId,
	ColumnWidthMap,
	CustomComponents,
	EventHandlers,
	OptimisticFieldSets,
	SortOrder,
} from '../common/types';
import {
	ColumnName,
	ContentContainer,
	HeaderCell,
	HeaderCellWithBorder,
} from '../common/ui/header-cell/styled';
import { IssueTableSkeleton } from '../common/ui/skeleton';
import { isKnownColumnType, isNonNullish } from '../common/utils';
import { SelectedRowContainer } from '../controllers/selected-row-state';
import { TableSizeProvider } from '../controllers/table-size';
import { LazyEditabilityLoaderContainer } from '../services/lazy-editability-loader';
import FooterRenderer from './footer-renderer';
import IssueTable from './issue-table';
import KeyboardShortcuts from './keyboard-shortcuts';
import messages from './messages';
import { getColumnConfig } from './utils';

export type Props = {
	/**
	 * Fragment key for a collection of issues. This is typically obtained by spreading the
	 * `ui_nativeIssueTable_NativeIssueTable_issues` fragment in a Relay query, and passing the data path that
	 * corresponds to the place where the fragment is spread in the query.
	 *
	 * For example, given the following query:
	 *
	 * @example
	 *     const data = useLazyLoadQuery<issueTableQuery>(
	 *         graphql`
	 *             query issueTableQuery(
	 *                 $cloudId: ID!
	 *                 $issueSearchInput: JiraIssueSearchInput!
	 *                 (... other query arguments)
	 *             ) {
	 *                 jira {
	 *                     issueSearchStable(cloudId: $cloudId, issueSearchInput: $issueSearchInput) {
	 *                         ...ui_nativeIssueTable_NativeIssueTable_issues
	 *                     }
	 *                 }
	 *             }
	 *         `,
	 *         {
	 *             cloudId: 'test-cloud-id',
	 *             issueSearchInput: { jql: 'project = Yum' },
	 *         },
	 *     );
	 *
	 * The consumer would need to pass `data.jira.issueSearchStable` to the `issues` prop.
	 *
	 * @see {@link https://relay.dev/docs/tutorial/fragments-1/} for more details on how to use fragment keys.
	 */
	issues: IssuesFragment;
	/**
	 * Fragment key for a collection of field sets (columns) that should be visible in the issue table. This is
	 * typically obtained by spreading the `ui_nativeIssueTable_NativeIssueTable_columns` fragment in a Relay query,
	 * and passing the data path that corresponds to the place where the fragment is spread in the query.
	 *
	 * For example, given the following query:
	 *
	 * @example
	 *     const data = useLazyLoadQuery<issueTableQuery>(
	 *         graphql`
	 *             query issueTableQuery(
	 *                 $cloudId: ID!
	 *                 $namespace: String!
	 *                 (... other query arguments)
	 *             ) {
	 *                 jira {
	 *                     issueSearchViewByNamespaceAndViewId(cloudId: $cloudId, namespace: $namespace) {
	 *                         fieldSets(filter: { fieldSetSelectedState: SELECTED }) {
	 *                             ...ui_nativeIssueTable_NativeIssueTable_columns
	 *                         }
	 *                     }
	 *                 }
	 *             }
	 *         `,
	 *         {
	 *             cloudId: 'test-cloud-id',
	 *             namespace: 'test-namespace',
	 *         },
	 *     );
	 *
	 * The consumer would need to pass `data.jira.issueSearchViewByNamespaceAndViewId.fieldSets` to the `columns` prop.
	 *
	 * @see {@link https://relay.dev/docs/tutorial/fragments-1/} for more details on how to use fragment keys.
	 */
	columns: ColumnsFragment;
	/**
	 * A map of of field IDs to column widths that can be used to override the column width information included in the
	 * column configuration.
	 *
	 * The issue table will check for a column width for each column in this order:
	 * 1. the `columnWidthOverrides` map
	 * 2. the relay data passed to the `columns` prop
	 * 3. the table's default width for that field's type
	 */
	columnWidthOverrides?: ColumnWidthMap;
	/**
	 * Optional base URL for table cells that link to an issue search (for example, clicking on a label). If not
	 * provided, this will default to the global issue navigator (`/issues` route).
	 */
	issueSearchBaseUrl?: string;
	/**
	 * Allows consumers to provide custom components to be rendered in certain parts of the issue table.
	 */
	components?: CustomComponents;
	/**
	 * If this prop is set to `true`, the issue table component will display a loading state. The loading state to be
	 * displayed will depend on whether initial data has been loaded:
	 * – On initial load, the component will display a skeleton.
	 * – After that, the loading state will be an overlay that prevents interaction with issues.
	 *
	 * Consumers will typically set this prop to `true` while fetching issue data.
	 *
	 * This prop defaults to `false`.
	 */
	loading?: boolean;
	/**
	 * This prop groups all the callback functions that can be invoked when a user event happens on an issue.
	 */
	eventHandlers?: EventHandlers;
	/**
	 * Controls whether the issue table cells should be inline editable. At the moment, this is only supported for the
	 * Status field. If this is `false`, issue table cells will be rendered with a read-only version instead.
	 *
	 * This prop defaults to `true`.
	 */
	hasInlineEditing?: boolean;
	/**
	 * This prop allows control of the selected row in the issue table from the outside. Typically used in combination
	 * with `onSelectedRowChange` to respond to internal changes.
	 */
	selectedRow?: number | null;
	/**
	 * JQL term of the main field issue data is sorted by. For example, if the JQL string is
	 * `ORDER BY component ASC, created DESC`, `sortField` would be `component`.
	 */
	sortField?: string;
	/**
	 * Sort direction for the main field issue data is sorted by. For example, if the JQL string is
	 * `ORDER BY component ASC, created DESC`, `sortDirection` would be `ASC`.
	 */
	sortDirection?: SortOrder;
	/**
	 * Callback invoked when the selected row in the issue table changes due to a user action, such as clicking on a
	 * row or using the arrow keys to navigate the collection of issues.
	 */
	onSelectedRowChange?: (rowIndex: number) => void;
	/**
	 * Callback invoked when there is a change in the list of visible columns – which can be due to the user adding a
	 * column to the table, removing a column from the table, or reordering the columns. This will typically require
	 * refreshing `issues` and `columns` data. If the list of visible columns needs to be persisted, it is the
	 * consumer's responsibility to do so.
	 *
	 * This function receives 3 arguments:
	 * - `columnIds`: list of column ids (a.k.a. field set ids) after the update
	 * - `changeType`: can be `MODIFY_COLUMNS` (adding or removing a column), or `REORDER_COLUMNS` (same columns but in
	 *   a different order) – this information can be used to determine whether a data refresh is needed (e.g. if it's a
	 *   reorder operation, all issue data should be present in the Relay store and no update should be needed)
	 * - `optimisticFieldSets`: when the issue table has all the necessary column information to perform an optimistic
	 *   update (for example, when reordering the columns), this argument will be provided to inform the consumer
	 *   that an optimistic update is possible; this object contains optimistic `fieldSets` which can be plugged into a
	 *   Relay mutation's `optimisticResponse`
	 *
	 * Note: presence of this callback is used to determine whether to provide column configuration capabilities,
	 * which includes if users can **edit** their columns in the column picker component, and drag & drop of table headers.
	 */
	onColumnsChange?: (
		columnIds: ColumnId[],
		changeType: ColumnConfigChangeType,
		optimisticFieldSets?: OptimisticFieldSets,
	) => void;
	/**
	 * Callback invoked after a user has completed a resize interaction with a column. i.e. When the user finishes
	 * dragging (releases the mouse button) or submits the change via the dialog.
	 *
	 * The `columnWidths` parameter passed to the callback will contain all of the columns with a custom width present
	 * in the table.
	 *
	 * The presence of this callback is used to determine whether the user can resize columns in the issue table.
	 * Enabling column resizing will also enable a fixed table layout.
	 */
	onColumnResize?: (columnWidths: ColumnWidthMap) => void;
	/**
	 * This callback function is invoked to reset the table columns to their default configuration.
	 * Note: Its presence is used as an indicator to display the "Restore Defaults" button in the column configuration popup.
	 */
	onRestoreDefaultColumns?: () => void;
	/**
	 * Callback invoked when a user action results in a need to navigate to a different page of issues. This callback
	 * will be invoked with the cursor to paginate to, and whether last issue in the result set should be selected (for
	 * example, if the user is trying to navigate from the first issue in a page to the last issue in the previous page).
	 */
	onPageChange?: (cursor: string, shouldSelectLastIssue: boolean) => void;
	/**
	 * Callback when the issue table is interactive.
	 */
	onReady?: () => void;
	/**
	 * Callback invoked when the user clicks on a table header to sort by a column. First argument is the JQL term of
	 * the field, and second argument is the sort direction.
	 */
	onSortChange?: (field: string, direction: SortOrder) => void;
	/**
	 * Hook that returns the callback to be invoked when options need to be retrieved for the column picker – for
	 * example, when opening the picker, searching for columns, or clicking the “Show more” button to load more columns.
	 *
	 * The first two arguments for this callback are the search term and the pagination cursor, which the consumer can
	 * use to fetch the relevant data. Once data is available, the consumer needs to invoke the callback provided as
	 * third argument with that data.
	 *
	 * Note: in case of pagination, only the new options need to be returned, and the column picker will merge those
	 * with the existing list of options.
	 *
	 * The presence of this callback is used to determine whether the user can **view** the column picker component
	 * and their column configurations.
	 */
	useColumnLoader?: ColumnPickerProps['useColumnLoader'];
	/**
	 * Whether the column picker is currently loading. This is used to display a loading state in the column picker.
	 */
	isColumnPickerLoading?: boolean;
	/**
	 * Key of the project where new issues should be created. When defined this will enable the issue create button in
	 * the table footer.
	 */
	issueCreateProjectKey?: string;
};

const NativeIssueTable = ({
	issues,
	columns,
	columnWidthOverrides,
	components,
	loading = false,
	selectedRow = null,
	eventHandlers,
	hasInlineEditing = true,
	onSelectedRowChange,
	onPageChange,
	sortField,
	sortDirection = SORT_ASC,
	onReady,
	onSortChange,
	onColumnsChange,
	onColumnResize,
	onRestoreDefaultColumns,
	issueSearchBaseUrl = '/issues',
	useColumnLoader,
	isColumnPickerLoading,
	issueCreateProjectKey,
}: Props) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLTableElement>(null);

	const { formatMessage } = useIntl();

	const issuesData = useFragment<IssuesFragment>(
		graphql`
			fragment ui_nativeIssueTable_NativeIssueTable_issues on JiraIssueConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
				pageSize: { type: "Int" }
			) {
				edges {
					node {
						key
					}
				}
				...issueTable_nativeIssueTable @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
				...footerRenderer_nativeIssueTable @arguments(pageSize: $pageSize)
				...selectedRowState_nativeIssueTable_SelectedRowContainer @arguments(pageSize: $pageSize)
			}
		`,
		issues,
	);

	/* eslint-disable jira/relay/must-colocate-fragment-spreads */
	const columnsData = useFragment<ColumnsFragment>(
		graphql`
			fragment ui_nativeIssueTable_NativeIssueTable_columns on JiraIssueSearchFieldSetConnection {
				totalCount @required(action: THROW)
				edges @required(action: THROW) {
					node @required(action: THROW) {
						fieldSetId @required(action: THROW)
						type @required(action: THROW)
						displayName @required(action: THROW)
						jqlTerm @required(action: THROW)
						isSortable
					}
				}
				...main_issueTableColumnPicker_ColumnConfig
			}
		`,
		columns,
	);
	/* eslint-enable jira/relay/must-colocate-fragment-spreads */

	const isInitialDataLoaded = useRef(false);

	useEffect(() => {
		if (
			ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') &&
			!loading &&
			!isInitialDataLoaded.current
		) {
			isInitialDataLoaded.current = true;
		}
	}, [loading]);

	const analyticsAttributes = useMemo(() => {
		const issueTableNumberOfColumns = columnsData.totalCount;
		const issueTableFieldTypesSet = new Set<string>();
		columnsData.edges
			.map((edge) => edge?.node.type)
			.filter(isNonNullish)
			.forEach(issueTableFieldTypesSet.add, issueTableFieldTypesSet);
		const issueTableFieldTypes = Array.from(issueTableFieldTypesSet).sort();

		return { issueTableNumberOfColumns, issueTableFieldTypes };
	}, [columnsData.edges, columnsData.totalCount]);

	const canViewColumnConfiguration = useColumnLoader !== undefined;
	const canEditColumnConfiguration = onColumnsChange !== undefined;
	const isColumnResizingEnabled = ff('nin-resizable-columns_8z4eo') && onColumnResize != null;
	const hasFixedTableLayout = ff('nin-resizable-columns_8z4eo') && isColumnResizingEnabled;

	const onColumnPickerChange = useCallback(
		(columnIds: string[] | undefined) => {
			if (onColumnsChange && columnIds) {
				onColumnsChange(columnIds, MODIFY_COLUMNS);
			}
		},
		[onColumnsChange],
	);

	const getHeaderCellForNoColumnConfig = useCallback(
		() =>
			ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? (
				<ContentContainer isIconPersistent={false}>
					<ColumnName aria-label={formatMessage(messages.columnHeaderIssueActions)} />
				</ContentContainer>
			) : (
				<HeaderCell scope="col" alignRight>
					<ContentContainer isIconPersistent={false}>
						<ColumnName>{formatMessage(messages.columnHeaderIssueActions)}</ColumnName>
					</ContentContainer>
				</HeaderCell>
			),
		[formatMessage],
	);

	const mappedColumns = useMemo(() => {
		const columnConfig = getColumnConfig(formatMessage);

		const columnsWithConfig: Array<Column> = [];

		columnsData.edges
			.map((edge) => edge?.node)
			.filter(isNonNullish)
			.forEach((node) => {
				const { fieldSetId, type, displayName, jqlTerm, isSortable } = node;
				const baseConfig = {
					id: fieldSetId,
					type,
					title: displayName ?? '',
					jqlTerm,
					fieldTypeDisplayName: '',
					isSortable: isSortable ?? undefined,
					currentSorting: jqlTerm === sortField ? sortDirection : SORT_NONE,
					onSortOrderChanged: onSortChange,
					...(!ff('nin-resizable-columns_8z4eo') && {
						// added with inline editing, removed with resizable columns
						shouldResizeWithTable: true,
					}),
					...(ff('com.atlassian.rm.jpo.deprecate-parent-link-nin') &&
						type === COLUMN_TYPE_PARENT_LINK && {
							warningText: formatMessage(messages.deprecatedParentLinkMessage, {
								strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
							}),
						}),
					...(ff('nin-resizable-columns_8z4eo') && {
						// TODO EM-6714: Update to also check for value from API
						customWidth: columnWidthOverrides?.[fieldSetId],
					}),
				};

				if (isKnownColumnType(type)) {
					const getColumnTypeConfig = columnConfig[type];
					const knownColumnTypeConfig = getColumnTypeConfig();
					if (ff('nin-resizable-columns_8z4eo')) {
						// Prepare for removing `width` and `shouldResizeWithTable` from default
						// column configs, can be cleaned up with nin-resizable-columns_8z4eo
						delete knownColumnTypeConfig.width;
						delete knownColumnTypeConfig.shouldResizeWithTable;
					}
					columnsWithConfig.push({
						...baseConfig,
						...knownColumnTypeConfig,
						isSortable: knownColumnTypeConfig.isSortable ?? true,
					});
				} else {
					columnsWithConfig.push({
						...baseConfig,
						defaultWidth: DEFAULT_COLUMN_WIDTH,
						...(!ff('nin-resizable-columns_8z4eo') && { width: DEFAULT_COLUMN_WIDTH }),
					});
				}
			});

		const MaybeColumnPickerCell = ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')
			? Fragment
			: ColumnPickerCell;

		const customHeader = canViewColumnConfiguration ? (
			<MaybeColumnPickerCell>
				<AsyncColumnPicker
					columns={columnsData}
					components={components?.columnPicker}
					onChange={canEditColumnConfiguration ? onColumnPickerChange : undefined}
					onRestoreDefaultColumns={onRestoreDefaultColumns}
					useColumnLoader={useColumnLoader}
					isLoading={isColumnPickerLoading}
				/>
			</MaybeColumnPickerCell>
		) : (
			getHeaderCellForNoColumnConfig()
		);

		columnsWithConfig.push({
			id: COLUMN_ID_MEATBALL_MENU,
			type: KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS,
			title: '',
			fieldTypeDisplayName: '',
			jqlTerm: '',
			// TODO: inline custom header wrapper when cleaning up jsv-one-issue-table-spreadsheet-ui_ygrnf
			customHeader: ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? (
				<Inline alignInline="end">{customHeader}</Inline>
			) : (
				customHeader
			),
			...columnConfig[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS](),
		});

		return columnsWithConfig;
	}, [
		canEditColumnConfiguration,
		canViewColumnConfiguration,
		columnsData,
		columnWidthOverrides,
		components?.columnPicker,
		formatMessage,
		getHeaderCellForNoColumnConfig,
		isColumnPickerLoading,
		onColumnPickerChange,
		onRestoreDefaultColumns,
		onSortChange,
		sortDirection,
		sortField,
		useColumnLoader,
	]);

	const handleOnColumnsChange = useCallback(
		(newColumns: Column[]) => {
			if (onColumnsChange) {
				const columnsWithoutMeatball = newColumns.filter(
					({ id }) => Boolean(id) && id !== COLUMN_ID_MEATBALL_MENU,
				);

				const columnIds = columnsWithoutMeatball.map(({ id }) => id);

				const edges = columnsWithoutMeatball.map(
					({ id, type, title, jqlTerm, isSortable, fieldTypeDisplayName }) => ({
						node: {
							fieldSetId: id,
							type: type ?? null,
							displayName: title,
							jqlTerm: jqlTerm ?? null,
							isSortable: isSortable ?? null,
							fieldType: fieldTypeDisplayName
								? {
										displayName: fieldTypeDisplayName,
									}
								: null,
						},
					}),
				);

				const optimisticFieldSets = {
					totalCount: columnsData.totalCount,
					edges,
				};

				onColumnsChange(columnIds, REORDER_COLUMNS, optimisticFieldSets);
			}
		},
		[columnsData.totalCount, onColumnsChange],
	);

	const handleOnResizeColumn = useCallback(
		(fieldSetId: string, newWidth: number | undefined) => {
			if (onColumnResize) {
				// Collect custom widths from the mapped columns object
				const mappedColumnWidths = mappedColumns.reduce<ColumnWidthMap>((acc, column) => {
					if (column.customWidth != null && column.customWidth !== column.defaultWidth) {
						acc[column.id] = column.customWidth;
					}
					return acc;
				}, {});

				// Update or remove the new width from the custom widths object
				if (newWidth != null) {
					mappedColumnWidths[fieldSetId] = newWidth;
				} else {
					delete mappedColumnWidths[fieldSetId];
				}

				onColumnResize(mappedColumnWidths);
			}
		},
		[onColumnResize, mappedColumns],
	);

	const issueKeys = useMemo(
		() => issuesData?.edges?.map((edge) => edge?.node?.key ?? ''),
		[issuesData?.edges],
	);

	const tableInjectedProps = useMemo(
		() => ({
			...eventHandlers,
			issueSearchBaseUrl,
		}),
		[eventHandlers, issueSearchBaseUrl],
	);

	useEffect(() => {
		ff('nin.page-load-instrumentation-fix') && onReady && onReady();
	}, [onReady]);

	const conditionalOnColumnsChange = canEditColumnConfiguration ? handleOnColumnsChange : undefined;

	useEffect(() => {
		const scrollableElement = scrollContainerRef.current;

		if (scrollableElement) {
			return autoScrollForElements({
				element: scrollableElement,
				// Don't allow scroll for column resizing because the calculations are be based on
				// mouse position and the mouse doesn't actually move when the autoScroll occurs.
				...(ff('nin-resizable-columns_8z4eo') && {
					canScroll: ({ source }) => source.data.operation !== COLUMN_OPERATION_RESIZE,
				}),
			});
		}
	}, []);

	// Show the skeleton if we are loading on initial render as we may only have partial row data
	if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && loading && !isInitialDataLoaded.current) {
		return <IssueTableSkeleton />;
	}

	const enableRowDragging = ff('jsw.enable-nin-rank-dnd') && sortField?.toLowerCase() === 'rank';

	// TODO: inline footer component when cleaning up jsv-one-issue-table-spreadsheet-ui_ygrnf
	const footer = (
		<FooterRenderer
			Footer={components?.Footer}
			IssueCount={components?.IssueCount}
			issues={issuesData}
			issueCreateProjectKey={issueCreateProjectKey}
			onPageChange={onPageChange}
		/>
	);

	const MaybeMainContainer = ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')
		? MainContainer
		: Fragment;

	const content = (
		<ContextualAnalyticsData
			sourceName="nativeIssueTable"
			sourceType={SCREEN}
			attributes={analyticsAttributes}
		>
			<MaybeMainContainer>
				<ScrollContainer ref={scrollContainerRef}>
					<LazyEditabilityLoaderContainer isGlobal>
						<SelectedRowContainer
							selectedRowIndex={selectedRow}
							amountOfRows={issuesData?.edges?.length ?? 0}
							onRowChange={onSelectedRowChange}
							onPageChange={onPageChange}
							issues={issuesData}
						>
							<KeyboardShortcuts
								issueKeys={issueKeys}
								onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
							/>
							<ColumnPickerContainer>
								<IssueTable
									issueResults={issuesData}
									columns={mappedColumns}
									canViewColumnConfiguration={canViewColumnConfiguration}
									canEditColumnConfiguration={canEditColumnConfiguration}
									hasInlineEditing={hasInlineEditing}
									hasFixedTableLayout={hasFixedTableLayout}
									isColumnResizingEnabled={isColumnResizingEnabled}
									loading={loading}
									tableInjectedProps={tableInjectedProps}
									onColumnsChange={conditionalOnColumnsChange}
									onResizeColumn={handleOnResizeColumn}
									enableRowDragging={enableRowDragging}
									NoColumns={components?.NoColumns}
									NoIssues={components?.NoIssues}
									tableRef={tableRef}
								/>
							</ColumnPickerContainer>
						</SelectedRowContainer>
					</LazyEditabilityLoaderContainer>
					{!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && footer}
				</ScrollContainer>
				{ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && footer}
			</MaybeMainContainer>
			<FireUiAnalytics actionSubject="screen" action="viewed" actionSubjectId="nativeIssueTable" />
		</ContextualAnalyticsData>
	);

	return ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? (
		<ScrollStateContextProvider scrollRef={scrollContainerRef}>
			<TableSizeProvider tableRef={tableRef}>{content}</TableSizeProvider>
		</ScrollStateContextProvider>
	) : (
		content
	);
};

export default NativeIssueTable;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnPickerCell = styled(HeaderCellWithBorder)({
	right: 0,
	position: 'sticky',
	display: 'table-cell',
	backgroundColor: token('elevation.surface', colors.N0),
	paddingTop: token('space.0', '0'),
	paddingRight: token('space.0', '0'),
	paddingBottom: token('space.0', '0'),
	paddingLeft: token('space.075', '6px'),
	'> div': {
		// align to the right of the cell
		marginLeft: 'auto',
		width: 'fit-content',
		overflow: 'hidden',
		// min 2px padding needed on all sides to make room for focus border
		paddingTop: token('space.025', '2px'),
		// extra right margin added to align with issue actions button with design token compliant measurements
		paddingRight: token('space.050', '4px'),
		paddingBottom: token('space.025', '2px'),
		paddingLeft: token('space.025', '2px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScrollContainer = styled.div(
	{
		display: 'flex',
		position: 'relative',
		flexFlow: 'column nowrap',
		overflow: 'auto',
	},
	() =>
		ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')
			? {
					// Since header is sticky, we need to specify a scroll padding that matches its height so that auto-scrolling
					// functionality accounts for this element in its calculations, otherwise header can overlap the selected row.
					scrollPaddingTop: `${HEADER_HEIGHT}px`,
				}
			: {
					height: '100%',
				},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MainContainer = styled.div({
	display: 'flex',
	flexFlow: 'column nowrap',
	borderColor: token('color.border', '#091e4221'),
	borderStyle: 'solid',
	borderWidth: BORDER_WIDTH,
	borderRadius: token('border.radius.200', '8px'),
	overflow: 'hidden',
});
